import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Descriptions, Tag, Button, Space, Spin, Timeline, Input, Select, Modal, Form, Row, Col, Divider, Alert, Upload, message as antMessage, Checkbox
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  SwapOutlined,
  MessageOutlined,
  FileAddOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  BugOutlined,
  GlobalOutlined,
  CodeOutlined,
  LinkOutlined,
  DesktopOutlined,
  MobileOutlined,
  ChromeOutlined,
  AppleOutlined,
  WindowsOutlined,
  AndroidOutlined,
  UploadOutlined,
  PaperClipOutlined
} from '@ant-design/icons';
import {
  loadTicketById,
  assignTicket,
  changeTicketStatus,
  addTicketComment,
  convertToTasks,
  updateTicket,
  deleteTicket,
  clearSelectedTicket
} from 'store/slices/SupportSlice';
import { format } from 'date-fns';
import axios from 'axios';

const { TextArea } = Input;
const { Option } = Select;

const SupportTicketDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const ticket = useSelector(state => state.support.selectedTicket);
  const loading = useSelector(state => state.support.ticketsLoading);
  const ticketStatuses = useSelector(state => state.support.ticketStatuses);

  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taskProjectMembers, setTaskProjectMembers] = useState([]);
  const [taskMembersLoading, setTaskMembersLoading] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const [assignForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [taskForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Comment attachment state
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [uploadingComment, setUploadingComment] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(loadTicketById(id));
      loadEmployeesData();
      loadProjectsData();
      loadCategoriesData();
    }

    return () => {
      dispatch(clearSelectedTicket());
    };
  }, [id, dispatch]);

  const loadEmployeesData = async () => {
    try {
      const response = await axios.get('/employee/employee/?page_size=100');
      setEmployees(response.data.results || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const loadProjectsData = async () => {
    try {
      const response = await axios.get('/project_board/project/?page_size=100');
      setProjects(response.data.results || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadCategoriesData = async () => {
    try {
      const response = await axios.get('/support/ticket-categories/?page_size=100');
      setCategories(response.data.results || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleAssign = async () => {
    try {
      const values = await assignForm.validateFields();
      await dispatch(assignTicket({
        ticketId: id,
        employee_id: values.employee_id,
        assigned_team: values.assigned_team
      })).unwrap();

      setAssignModalVisible(false);
      assignForm.resetFields();
      dispatch(loadTicketById(id));
    } catch (error) {
      console.error('Failed to assign:', error);
    }
  };

  const handleStatusChange = async () => {
    try {
      const values = await statusForm.validateFields();
      await dispatch(changeTicketStatus({
        ticketId: id,
        status_id: values.status_id,
        sub_status_id: values.sub_status_id
      })).unwrap();

      setStatusModalVisible(false);
      statusForm.resetFields();
      dispatch(loadTicketById(id));
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  const handleAddComment = async () => {
    try {
      const values = await commentForm.validateFields();
      const attachmentIds = commentAttachments.map(att => att.id);

      await dispatch(addTicketComment({
        ticketId: id,
        comment_type: values.is_internal ? 'INTERNAL_NOTE' : 'EMPLOYEE_REPLY',
        message: values.message,
        is_internal: values.is_internal || false,
        attachment_ids: attachmentIds
      })).unwrap();

      setCommentModalVisible(false);
      commentForm.resetFields();
      setCommentAttachments([]);
      dispatch(loadTicketById(id));
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleCommentAttachmentUpload = async (file) => {
    const formData = new FormData();
    formData.append('document_file', file);
    formData.append('name', file.name);
    formData.append('document_type', 'ATTACHMENT');

    setUploadingComment(true);
    try {
      const response = await axios.post('/document/document/', formData);
      setCommentAttachments(prev => [...prev, {
        id: response.data.id,
        name: file.name,
        size: file.size,
        uid: file.uid || response.data.id
      }]);
      antMessage.success(`${file.name} uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      antMessage.error(`Failed to upload ${file.name}`);
    } finally {
      setUploadingComment(false);
    }
    return false; // Prevent default upload
  };

  const removeCommentAttachment = (uid) => {
    setCommentAttachments(prev => prev.filter(att => att.uid !== uid && att.id !== uid));
  };

  // Load assignable members of the project picked in the Convert-to-Task modal.
  // Normalised the same way as TaskIssueDrawer: { id: user_id, user_name, employee_id }.
  const loadTaskProjectMembers = async (projectId) => {
    taskForm.setFieldsValue({ assignee_user_ids: [] });
    if (!projectId) {
      setTaskProjectMembers([]);
      return;
    }
    setTaskMembersLoading(true);
    try {
      const res = await axios.get(`/project_board/members/?project=${projectId}&page_size=1000`);
      const members = res.data.results || res.data || [];
      setTaskProjectMembers(
        members.map(m => ({
          id: m.user_id,
          user_name: m.user_name || m.user_email,
          employee_id: m.employee_id,
        })).filter(m => m.id)
      );
    } catch (error) {
      setTaskProjectMembers([]);
      antMessage.error('Failed to load project members');
    } finally {
      setTaskMembersLoading(false);
    }
  };

  const handleConvertToTasks = async () => {
    try {
      const values = await taskForm.validateFields();
      const tasks = [{
        project_id: values.project_id,
        title: values.title || ticket.title,
        description: values.description || ticket.description,
        notes: values.notes,
        assignee_user_ids: values.assignee_user_ids || []
      }];

      await dispatch(convertToTasks({ ticketId: id, tasks })).unwrap();

      setTaskModalVisible(false);
      taskForm.resetFields();
      setTaskProjectMembers([]);
      dispatch(loadTicketById(id));
    } catch (error) {
      console.error('Failed to convert to tasks:', error);
    }
  };

  const handleEdit = async () => {
    try {
      const values = await editForm.validateFields();
      await dispatch(updateTicket({
        ticketId: id,
        ticketData: values
      })).unwrap();

      setEditModalVisible(false);
      editForm.resetFields();
      dispatch(loadTicketById(id));
    } catch (error) {
      console.error('Failed to update ticket:', error);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Ticket',
      content: 'Are you sure you want to delete this ticket? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dispatch(deleteTicket(id)).unwrap();
          navigate('/support/tickets');
        } catch (error) {
          console.error('Failed to delete ticket:', error);
        }
      }
    });
  };

  const openEditModal = () => {
    editForm.setFieldsValue({
      title: ticket.title,
      description: ticket.description,
      priority: ticket.priority,
      category: ticket.category,
      component: ticket.component,
      issue_url: ticket.issue_url,
      steps_to_reproduce: ticket.steps_to_reproduce,
      expected_result: ticket.expected_result,
      actual_result: ticket.actual_result,
      console_logs: ticket.console_logs,
      environment_info: ticket.environment_info || {}
    });
    setEditModalVisible(true);
  };

  const getStatusColor = (statusName) => {
    const name = statusName?.toLowerCase() || '';
    if (name.includes('closed') || name.includes('resolved')) return 'green';
    if (name.includes('progress')) return 'blue';
    if (name.includes('pending')) return 'orange';
    return 'cyan';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'CRITICAL': 'red',
      'HIGH': 'orange',
      'MEDIUM': 'blue',
      'LOW': 'default'
    };
    return colors[priority] || 'default';
  };

  const getSLAStatusColor = (slaStatus) => {
    if (slaStatus === 'BREACHED') return 'red';
    if (slaStatus === 'APPROACHING_BREACH') return 'orange';
    return 'green';
  };

  if (loading && !ticket) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert message="Ticket not found" type="error" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/support/tickets')}
          style={{ marginBottom: 16 }}
        >
          Back to Tickets
        </Button>

        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: 28, margin: 0, marginBottom: 8 }}>
                {ticket.ticket_number}
              </h1>
              <h2 style={{ fontSize: 20, fontWeight: 'normal', margin: 0 }}>
                {ticket.title}
              </h2>
            </div>
            <Space>
              <Button
                icon={<EditOutlined />}
                onClick={openEditModal}
              >
                Edit
              </Button>
              <Button
                icon={<UserOutlined />}
                onClick={() => setAssignModalVisible(true)}
              >
                Assign
              </Button>
              <Button
                icon={<SwapOutlined />}
                onClick={() => setStatusModalVisible(true)}
              >
                Change Status
              </Button>
              <Button
                icon={<FileAddOutlined />}
                onClick={() => setTaskModalVisible(true)}
              >
                Convert to Task
              </Button>
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={handleDelete}
              >
                Delete
              </Button>
            </Space>
          </div>
        </Space>
      </div>

      <Row gutter={24}>
        {/* Main Content */}
        <Col span={16}>
          {/* Ticket Details */}
          <Card title="Ticket Information" style={{ marginBottom: 16 }}>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(ticket.status_name)}>
                  {ticket.status_name}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                <Tag color={getPriorityColor(ticket.priority)}>
                  {ticket.priority_display || ticket.priority}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {ticket.category_name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                {ticket.source_display || ticket.source}
              </Descriptions.Item>
              <Descriptions.Item label="Account">
                {ticket.account_name || ticket.account_details?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Requester">
                {ticket.requester_name} ({ticket.requester_email})
              </Descriptions.Item>
              <Descriptions.Item label="Assigned To">
                {ticket.assigned_to_name || ticket.assigned_to_details?.name || (
                  <span style={{ color: '#999' }}>Unassigned</span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="SLA Status">
                {ticket.sla_status_display ? (
                  <Tag color={getSLAStatusColor(ticket.sla_status)}>
                    {ticket.sla_status_display}
                  </Tag>
                ) : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At" span={2}>
                {format(new Date(ticket.created_at), 'MMM dd, yyyy hh:mm a')}
              </Descriptions.Item>
              {ticket.first_response_at && (
                <Descriptions.Item label="First Response">
                  {format(new Date(ticket.first_response_at), 'MMM dd, yyyy hh:mm a')}
                </Descriptions.Item>
              )}
              {ticket.resolved_at && (
                <Descriptions.Item label="Resolved At">
                  {format(new Date(ticket.resolved_at), 'MMM dd, yyyy hh:mm a')}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <div>
              <h4><BugOutlined style={{ marginRight: 8 }} />Description:</h4>
              <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
                {ticket.description}
              </div>
            </div>

            {/* Project & Component - Main Display */}
            {(ticket.project_details || ticket.component) && (
              <>
                <Divider />
                <Row gutter={16}>
                  {ticket.project_details && (
                    <Col span={12}>
                      <div>
                        <h4><GlobalOutlined style={{ marginRight: 8 }} />Project:</h4>
                        <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: 4 }}>
                          <Tag color="geekblue">{ticket.project_details.project_code}</Tag>
                          <span style={{ marginLeft: 8, fontWeight: 500 }}>{ticket.project_details.name}</span>
                        </div>
                      </div>
                    </Col>
                  )}
                  {ticket.component && (
                    <Col span={12}>
                      <div>
                        <h4><CodeOutlined style={{ marginRight: 8 }} />Component:</h4>
                        <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: 4 }}>
                          <Tag>{ticket.component}</Tag>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </>
            )}

            {/* Issue URL - Main Display */}
            {ticket.issue_url && (
              <>
                <Divider />
                <div>
                  <h4><LinkOutlined style={{ marginRight: 8 }} />Issue URL:</h4>
                  <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: 4 }}>
                    <a
                      href={ticket.issue_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {ticket.issue_url}
                    </a>
                  </div>
                </div>
              </>
            )}

            {/* Environment - Main Display */}
            {ticket.environment_info && Object.keys(ticket.environment_info).length > 0 && (
              <>
                <Divider />
                <div>
                  <h4><DesktopOutlined style={{ marginRight: 8 }} />Environment:</h4>
                  <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: 4 }}>
                    <Row gutter={24}>
                      {ticket.environment_info.os && (
                        <Col span={8}>
                          <div style={{ marginBottom: 4 }}>
                            <span style={{ color: '#666', fontSize: 12 }}>OS:</span>
                          </div>
                          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {ticket.environment_info.os.toLowerCase().includes('mac') ? (
                              <AppleOutlined />
                            ) : ticket.environment_info.os.toLowerCase().includes('windows') ? (
                              <WindowsOutlined />
                            ) : ticket.environment_info.os.toLowerCase().includes('android') ? (
                              <AndroidOutlined />
                            ) : (
                              <DesktopOutlined />
                            )}
                            {ticket.environment_info.os}
                          </div>
                        </Col>
                      )}
                      {ticket.environment_info.browser && (
                        <Col span={8}>
                          <div style={{ marginBottom: 4 }}>
                            <span style={{ color: '#666', fontSize: 12 }}>Browser:</span>
                          </div>
                          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <ChromeOutlined />
                            {ticket.environment_info.browser}
                          </div>
                        </Col>
                      )}
                      {ticket.environment_info.device && (
                        <Col span={8}>
                          <div style={{ marginBottom: 4 }}>
                            <span style={{ color: '#666', fontSize: 12 }}>Device:</span>
                          </div>
                          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {ticket.environment_info.device.toLowerCase().includes('mobile') ? (
                              <MobileOutlined />
                            ) : (
                              <DesktopOutlined />
                            )}
                            {ticket.environment_info.device}
                          </div>
                        </Col>
                      )}
                    </Row>
                    {ticket.environment_info.screen_resolution && (
                      <div style={{ marginTop: 12 }}>
                        <span style={{ color: '#666', fontSize: 12 }}>Screen Resolution:</span>
                        <div style={{ fontWeight: 500 }}>{ticket.environment_info.screen_resolution}</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {ticket.attachment_details && ticket.attachment_details.length > 0 && (
              <>
                <Divider />
                <div>
                  <h4>Attachments ({ticket.attachment_details.length}):</h4>
                  <Space wrap>
                    {ticket.attachment_details.map((att) => {
                      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(
                        att.name?.toLowerCase().split('.').pop()
                      );
                      return isImage ? (
                        <a
                          key={att.id}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={att.url}
                            alt={att.name}
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 4,
                              border: '1px solid #d9d9d9'
                            }}
                          />
                        </a>
                      ) : (
                        <a
                          key={att.id}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'inline-block', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 4 }}
                        >
                          📎 {att.name}
                        </a>
                      );
                    })}
                  </Space>
                </div>
              </>
            )}
          </Card>

          {/* Bug Report Details - only show if any bug fields are present */}
          {(ticket.steps_to_reproduce || ticket.expected_result || ticket.actual_result) && (
            <Card
              title={
                <Space>
                  <BugOutlined />
                  <span>Bug Report Details</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              {ticket.steps_to_reproduce && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ color: '#1890ff', marginBottom: 8 }}>Steps to Reproduce:</h4>
                  <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
                    {ticket.steps_to_reproduce}
                  </div>
                </div>
              )}

              <Row gutter={16}>
                {ticket.expected_result && (
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ color: '#52c41a', marginBottom: 8 }}>Expected Result:</h4>
                      <div style={{ padding: '12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
                        {ticket.expected_result}
                      </div>
                    </div>
                  </Col>
                )}

                {ticket.actual_result && (
                  <Col span={12}>
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ color: '#ff4d4f', marginBottom: 8 }}>Actual Result:</h4>
                      <div style={{ padding: '12px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
                        {ticket.actual_result}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </Card>
          )}

          {/* Console Logs */}
          {ticket.console_logs && (
            <Card
              title={
                <Space>
                  <CodeOutlined />
                  <span>Console Logs / Errors</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              <pre style={{
                padding: '12px',
                background: '#1e1e1e',
                color: '#d4d4d4',
                borderRadius: 4,
                overflow: 'auto',
                maxHeight: '300px',
                fontSize: '12px',
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
              }}>
                {ticket.console_logs}
              </pre>
            </Card>
          )}

          {/* Comments/Timeline */}
          <Card title="Conversation & Activity">
            {ticket.comments && ticket.comments.length > 0 ? (
              <Timeline>
                {ticket.comments.map((comment) => (
                  <Timeline.Item
                    key={comment.id}
                    color={
                      comment.comment_type === 'SYSTEM_MESSAGE' ? 'gray' :
                      comment.comment_type === 'EMPLOYEE_REPLY' ? 'green' :
                      comment.comment_type === 'INTERNAL_NOTE' ? 'orange' :
                      'blue'
                    }
                    dot={
                      comment.is_internal ? <ExclamationCircleOutlined /> :
                      comment.comment_type === 'EMPLOYEE_REPLY' ? <CheckCircleOutlined /> :
                      <ClockCircleOutlined />
                    }
                  >
                    <div style={{ marginBottom: 8 }}>
                      <strong>{comment.created_by_name || 'System'}</strong>
                      {comment.is_internal && (
                        <Tag color="orange" style={{ marginLeft: 8 }}>Internal Note</Tag>
                      )}
                      <Tag color={
                        comment.comment_type === 'EMPLOYEE_REPLY' ? 'green' :
                        comment.comment_type === 'CUSTOMER_REPLY' ? 'blue' :
                        'default'
                      }>
                        {comment.comment_type_display || comment.comment_type}
                      </Tag>
                      <span style={{ color: '#999', marginLeft: 8 }}>
                        {format(new Date(comment.created_at), 'MMM dd, yyyy hh:mm a')}
                      </span>
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
                      {comment.message}
                    </div>
                    {/* Comment Attachments */}
                    {comment.attachment_details && comment.attachment_details.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <p style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                          <PaperClipOutlined /> Attachments ({comment.attachment_details.length})
                        </p>
                        <Space wrap>
                          {comment.attachment_details.map((att) => {
                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(
                              att.name?.toLowerCase().split('.').pop()
                            );
                            return isImage ? (
                              <a
                                key={att.id}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={att.url}
                                  alt={att.name}
                                  style={{
                                    width: 80,
                                    height: 80,
                                    objectFit: 'cover',
                                    borderRadius: 4,
                                    border: '1px solid #d9d9d9'
                                  }}
                                />
                              </a>
                            ) : (
                              <a
                                key={att.id}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  padding: '4px 8px',
                                  border: '1px solid #d9d9d9',
                                  borderRadius: 4,
                                  fontSize: 12
                                }}
                              >
                                <PaperClipOutlined style={{ marginRight: 4 }} />
                                {att.name}
                              </a>
                            );
                          })}
                        </Space>
                      </div>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <p style={{ color: '#999' }}>No comments yet</p>
            )}

            <Divider />

            <Button
              icon={<MessageOutlined />}
              type="primary"
              onClick={() => setCommentModalVisible(true)}
              block
            >
              Add Comment
            </Button>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col span={8}>
          {/* Customer Info */}
          <Card title="Customer Information" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Account">
                {ticket.account_details?.name || ticket.account_name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {ticket.account_details?.email || ticket.requester_email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {ticket.account_details?.phone || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Account Tier">
                <Tag color="blue">
                  {ticket.account_details?.account_tier_display || 'Standard'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* SLA Information */}
          {ticket.sla_status && ticket.sla_status !== 'NO_SLA' && (
            <Card title="SLA Status" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <strong>Status:</strong>{' '}
                  <Tag color={
                    ticket.sla_status === 'BREACHED' ? 'red' :
                    ticket.sla_status === 'APPROACHING_BREACH' ? 'orange' :
                    'green'
                  }>
                    {ticket.sla_status_display || ticket.sla_status}
                  </Tag>
                </div>
                {ticket.sla_breach_at && (
                  <div>
                    <strong>Breach Deadline:</strong><br />
                    {format(new Date(ticket.sla_breach_at), 'MMM dd, yyyy hh:mm a')}
                  </div>
                )}
              </Space>
            </Card>
          )}

          {/* Related Tasks */}
          {ticket.related_tasks_details && ticket.related_tasks_details.length > 0 && (
            <Card title="Related Tasks" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {ticket.related_tasks_details.map((task) => (
                  <div key={task.id} style={{ padding: '8px', background: '#f5f5f5', borderRadius: 4 }}>
                    <strong>{task.task_code}</strong>
                    <div style={{ fontSize: 12, color: '#666' }}>{task.task_title}</div>
                    <Tag size="small">{task.task_status}</Tag>
                  </div>
                ))}
              </Space>
            </Card>
          )}

          {/* Satisfaction Rating */}
          {ticket.satisfaction_rating && (
            <Card title="Customer Satisfaction" style={{ marginBottom: 16 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>
                  {'⭐'.repeat(ticket.satisfaction_rating)}
                  {'☆'.repeat(5 - ticket.satisfaction_rating)}
                </div>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {ticket.satisfaction_rating}/5
                </div>
                {ticket.satisfaction_feedback && (
                  <div style={{ marginTop: 12, padding: '8px', background: '#f5f5f5', borderRadius: 4, textAlign: 'left' }}>
                    <em>"{ticket.satisfaction_feedback}"</em>
                  </div>
                )}
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Assign Modal */}
      <Modal
        title="Assign Ticket"
        open={assignModalVisible}
        onOk={handleAssign}
        onCancel={() => setAssignModalVisible(false)}
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="employee_id"
            label="Assign to Employee"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              placeholder="Select employee"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {employees.map(emp => (
                <Option key={emp.id} value={emp.id}>
                  {emp.user?.first_name} {emp.user?.last_name} ({emp.employee_id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="assigned_team" label="Team (Optional)">
            <Input placeholder="e.g., Technical Support, Billing" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Status Change Modal */}
      <Modal
        title="Change Ticket Status"
        open={statusModalVisible}
        onOk={handleStatusChange}
        onCancel={() => setStatusModalVisible(false)}
      >
        <Form form={statusForm} layout="vertical">
          <Form.Item
            name="status_id"
            label="New Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status">
              {ticketStatuses.map(status => (
                <Option key={status.id} value={status.id}>
                  {status.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Comment Modal */}
      <Modal
        title="Add Comment"
        open={commentModalVisible}
        onOk={handleAddComment}
        onCancel={() => {
          setCommentModalVisible(false);
          setCommentAttachments([]);
        }}
        width={600}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
        confirmLoading={uploadingComment}
      >
        <Form form={commentForm} layout="vertical">
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please enter a message' }]}
          >
            <TextArea rows={6} placeholder="Type your message..." />
          </Form.Item>

          <Form.Item label="Attachments">
            <Upload.Dragger
              multiple
              beforeUpload={handleCommentAttachmentUpload}
              fileList={commentAttachments.map(att => ({
                uid: att.uid || att.id,
                name: att.name,
                status: 'done',
                size: att.size
              }))}
              onRemove={(file) => {
                removeCommentAttachment(file.uid);
                return true;
              }}
              showUploadList={{
                showRemoveIcon: true,
                showDownloadIcon: false
              }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click or drag files to upload</p>
              <p className="ant-upload-hint">Support for images, documents, and other files</p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item name="is_internal" valuePropName="checked" initialValue={false}>
            <Checkbox>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 4 }} />
              Internal Note (not visible to customer)
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Convert to Task Modal */}
      <Modal
        title="Convert to Task"
        open={taskModalVisible}
        onOk={handleConvertToTasks}
        onCancel={() => setTaskModalVisible(false)}
        width={600}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      >
        <Form form={taskForm} layout="vertical">
          <Form.Item
            name="project_id"
            label="Project"
            rules={[{ required: true, message: 'Please select a project' }]}
          >
            <Select placeholder="Select project" onChange={loadTaskProjectMembers}>
              {projects.map(proj => (
                <Option key={proj.id} value={proj.id}>
                  {proj.project_code} - {proj.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="assignee_user_ids" label="Assignees (Optional)">
            <Select
              mode="multiple"
              allowClear
              placeholder={taskForm.getFieldValue('project_id') ? 'Select assignees' : 'Select a project first'}
              loading={taskMembersLoading}
              disabled={!taskProjectMembers.length && !taskMembersLoading}
              optionFilterProp="label"
              options={taskProjectMembers.map(m => ({ value: m.id, label: m.user_name }))}
            />
          </Form.Item>

          <Form.Item name="title" label="Task Title" initialValue={ticket.title}>
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Task Description" initialValue={ticket.description}>
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item name="notes" label="Notes (Optional)">
            <TextArea rows={2} placeholder="Why this task was created from the ticket..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Ticket Modal */}
      <Modal
        title="Edit Ticket"
        open={editModalVisible}
        onOk={handleEdit}
        onCancel={() => setEditModalVisible(false)}
        width={900}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Form form={editForm} layout="vertical">
          {/* Project Info (read-only display) */}
          {ticket?.project_details && (
            <Alert
              type="info"
              showIcon
              message={`Project: ${ticket.project_details.name} (${ticket.project_details.project_code})`}
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter ticket title' }]}
          >
            <Input placeholder="Ticket title" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter ticket description' }]}
          >
            <TextArea rows={4} placeholder="Describe the issue or request..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select priority' }]}
              >
                <Select placeholder="Select priority">
                  <Option value="LOW">Low</Option>
                  <Option value="MEDIUM">Medium</Option>
                  <Option value="HIGH">High</Option>
                  <Option value="CRITICAL">Critical</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="category" label="Category">
                <Select placeholder="Select category" allowClear>
                  {categories.map(cat => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="component" label="Component">
                <Select placeholder="Select component" allowClear>
                  {(ticket?.project_details?.components || ['Frontend', 'Backend', 'API', 'Database', 'Mobile App', 'Other']).map(comp => (
                    <Option key={comp} value={comp}>{comp}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="issue_url" label="Issue URL">
            <Input placeholder="URL where the issue occurred" />
          </Form.Item>

          <Divider style={{ margin: '16px 0' }}>Bug Details</Divider>

          <Form.Item name="steps_to_reproduce" label="Steps to Reproduce">
            <TextArea rows={3} placeholder="1. Go to...&#10;2. Click on...&#10;3. Observe..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="expected_result" label="Expected Result">
                <TextArea rows={3} placeholder="What should have happened?" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="actual_result" label="Actual Result">
                <TextArea rows={3} placeholder="What actually happened?" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="console_logs" label="Console Logs / Error Messages">
            <TextArea rows={3} placeholder="Error messages..." style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </Form.Item>

          <Divider style={{ margin: '16px 0' }}>Environment</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name={['environment_info', 'os']} label="OS">
                <Select allowClear>
                  <Option value="Windows">Windows</Option>
                  <Option value="macOS">macOS</Option>
                  <Option value="Linux">Linux</Option>
                  <Option value="Android">Android</Option>
                  <Option value="iOS">iOS</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['environment_info', 'browser']} label="Browser">
                <Select allowClear>
                  <Option value="Chrome">Chrome</Option>
                  <Option value="Firefox">Firefox</Option>
                  <Option value="Safari">Safari</Option>
                  <Option value="Edge">Edge</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['environment_info', 'device']} label="Device">
                <Select allowClear>
                  <Option value="Desktop">Desktop</Option>
                  <Option value="Mobile">Mobile</Option>
                  <Option value="Tablet">Tablet</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SupportTicketDetail;
